import React, { useState } from "react";
import styles from "../EmailValidation/EmailValidation.module.scss";
import Button from "../Forms/Button/Button.js";
import { motion } from "framer-motion";
import { fireAnalytics } from "../../../utils/firebase.js";
import { useGoogleLogin, TokenResponse } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import { useEmailValidation } from "../../../hooks/registration/useEmailValidation.ts";
import { useRegistrationFlow } from "../../../hooks/registration/useRegistrationFlow.ts";
import { loginWithGoogle } from "../../../store/actions/authActions.js";
import { useDispatch } from "react-redux";
import RegistrationInput from "../RegistrationInput/RegistrationInput.tsx";

const formVariant = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.1,
    },
  },
};

const itemVariant = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
  },
};

interface EmailValidationV2Props {
  onGoogleSuccess?: (accessToken: string) => void;
}

const EmailValidationV2: React.FC<EmailValidationV2Props> = ({
  onGoogleSuccess,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const registrationFlow = useRegistrationFlow();
  const { handleEmailSubmit, emailError, isSubmitting, setEmailError } = useEmailValidation({
    createPreRegister: registrationFlow.createPreRegister,
    setEmail: registrationFlow.setEmail,
    setStep: registrationFlow.setStep,
    preRegisterLoading: registrationFlow.preRegister.isLoading,
  });
  const [email, setEmail] = useState<string>("");

  const login = useGoogleLogin({
    onSuccess: (tokenResponse: TokenResponse) => {
      fireAnalytics("registro_google_iniciado", { category: "usuario" });
      if (onGoogleSuccess && tokenResponse.access_token) {
        onGoogleSuccess(tokenResponse.access_token);
      } else if (tokenResponse.access_token) {
        (dispatch as any)(loginWithGoogle(tokenResponse.access_token, navigate));
      }
    },
    onError: () => {
      fireAnalytics("registro_google_error", { category: "usuario" });
    },
    scope: 'openid email profile',
  });

  const handleGoogleClick = () => {
    login();
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      setEmailError("");
    }
  };

  const handleEmailBlur = (): void => {
    if (email && emailError) {
      setEmailError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      await handleEmailSubmit(email);
    } catch (error) {
    }
  };

  const isLoading = isSubmitting || registrationFlow.preRegister.isLoading;

  return (
    <motion.div
      variants={formVariant}
      initial="hidden"
      animate="visible"
      className={styles.container}
    >
      <motion.div variants={itemVariant} className={styles.content}>
        <motion.form variants={itemVariant} className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <RegistrationInput
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              error={emailError}
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || !email || !!emailError}
            className={styles.btn}
            colored
            bordered={false}
            link={false}
            typebtn="primary"
          >
            {isLoading ? "Comenzando..." : "Comenzar"}
          </Button>
        </motion.form>

        <motion.div variants={itemVariant} className={styles.googleDivider}>
          <span className={styles.googleDivider_line}></span>
          <div className={styles.googleDivider_text}>O</div>
          <span className={styles.googleDivider_line}></span>
        </motion.div>

        <motion.button
          variants={itemVariant}
          className={styles.googleButton}
          onClick={handleGoogleClick}
          disabled={isLoading}
          type="button"
        >
          <div className={styles.googleButton_content}>
            <svg
              className={styles.googleButton_icon}
              viewBox="0 0 24 24"
              width="24"
              height="24"
            >
              <path
                fill="#234ba2"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#2d5bc0"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#eef3ff"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#1e3d8a"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className={styles.googleButton_text}>
              Continuar con Google
            </span>
          </div>
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default EmailValidationV2;
